// The lead flow's step engine: which questions these answers need, in
// what order, and where a URL is allowed to land. Pure, so the whole
// walk is unit-tested without a browser. The browser's history is the
// flow's history (one route per step), exactly as in the quote flow;
// nothing here knows about React or the router.

import type { Answers, LovedOneAnswers, SelfAnswers } from "./answers";
import {
  isValidDob,
  isValidEmail,
  isValidName,
  isValidRelationOtherText,
  isValidPhone,
  isValidReference,
  isValidZip,
} from "./validation";

export type QuestionStepId =
  | "start"
  | "who-passed"
  | "where"
  | "relation"
  | "ready"
  | "preplan"
  | "planning-for"
  | "you"
  | "you-health"
  | "loved"
  | "loved-health"
  | "loved-details"
  | "loved-relation"
  | "contact";

export type TerminalStepId = "out-of-area" | "other-options" | "done";
export type StepId = QuestionStepId | TerminalStepId;

const QUESTION_STEPS: readonly QuestionStepId[] = [
  "start",
  "who-passed",
  "where",
  "relation",
  "ready",
  "preplan",
  "planning-for",
  "you",
  "you-health",
  "loved",
  "loved-health",
  "loved-details",
  "loved-relation",
  "contact",
];

export function isQuestionStep(slug: string): slug is QuestionStepId {
  return (QUESTION_STEPS as readonly string[]).includes(slug);
}

/** Whether the visitor is asked about themselves, and about a loved one. */
function includesSelf(a: Answers): boolean {
  return a.planningFor === "self" || a.planningFor === "both";
}

function includesLovedOne(a: Answers): boolean {
  return a.planningFor === "loved_one" || a.planningFor === "both";
}

/** The ordered question steps these answers need; terminal screens are
 * not part of the walk. */
export function stepsFor(a: Answers): QuestionStepId[] {
  if (a.situation === null) return ["start"];
  if (a.situation === "death_occurred") {
    return [
      "start",
      "who-passed",
      "where",
      "relation",
      "ready",
      "preplan",
      "contact",
    ];
  }
  const steps: QuestionStepId[] = ["start", "planning-for"];
  if (a.planningFor === null) return steps;
  if (includesSelf(a)) steps.push("you", "you-health");
  if (includesLovedOne(a)) {
    steps.push("loved", "loved-health", "loved-details");
    if (a.lovedOne.hospice === true) steps.push("loved-relation");
  }
  steps.push("contact");
  return steps;
}

/** Whether the contact step also asks for the submitter's name. A person
 * planning for themselves has already given it. */
export function needsSubmitterName(a: Answers): boolean {
  return a.situation === "death_occurred" || a.planningFor === "loved_one";
}

/** The ZIP the loved one lives in: the submitter's own when planning for
 * both and they share it. */
export function lovedOneZip(a: Answers): string {
  return a.planningFor === "both" && a.lovedOne.livesInMyZip
    ? a.self.zip
    : a.lovedOne.zip;
}

function hasNames(person: { firstName: string; lastName: string }): boolean {
  return isValidName(person.firstName) && isValidName(person.lastName);
}

function selfComplete(self: SelfAnswers): boolean {
  return hasNames(self) && isValidDob(self.dob) && isValidZip(self.zip);
}

function lovedDetailsComplete(a: Answers, loved: LovedOneAnswers): boolean {
  return isValidDob(loved.dob) && isValidZip(lovedOneZip(a));
}

export function isStepComplete(a: Answers, step: QuestionStepId): boolean {
  switch (step) {
    case "start":
      return a.situation !== null;
    case "who-passed":
      return hasNames(a.decedent);
    case "where":
      return a.where.type !== null && isValidZip(a.where.zip);
    case "relation":
      return (
        a.relation.type !== null &&
        (a.relation.type !== "other" ||
          isValidRelationOtherText(a.relation.otherText))
      );
    case "ready":
      return a.readyForCare !== null;
    case "preplan":
      return a.preplanned !== null && isValidReference(a.preplanReference);
    case "planning-for":
      return a.planningFor !== null;
    case "you":
      return selfComplete(a.self);
    case "you-health":
      return a.self.hospice !== null;
    case "loved":
      return hasNames(a.lovedOne);
    case "loved-health":
      return a.lovedOne.hospice !== null;
    case "loved-details":
      return lovedDetailsComplete(a, a.lovedOne);
    case "loved-relation":
      return (
        a.lovedOne.relation.type !== null &&
        (a.lovedOne.relation.type !== "other" ||
          isValidRelationOtherText(a.lovedOne.relation.otherText))
      );
    case "contact":
      return (
        isValidEmail(a.submitter.email) &&
        isValidPhone(a.submitter.phone) &&
        (!needsSubmitterName(a) || hasNames(a.submitter))
      );
  }
}

/** The first step still owed an answer, or the last step when every one
 * is answered. */
export function firstUnansweredStep(a: Answers): QuestionStepId {
  const steps = stepsFor(a);
  return (
    steps.find((step) => !isStepComplete(a, step)) ?? steps[steps.length - 1]
  );
}

export function nextStep(
  a: Answers,
  current: QuestionStepId,
): QuestionStepId | "submit" {
  const steps = stepsFor(a);
  const at = steps.indexOf(current);
  if (at === -1) return firstUnansweredStep(a);
  return at === steps.length - 1 ? "submit" : steps[at + 1];
}

export function prevStep(
  a: Answers,
  current: QuestionStepId,
): QuestionStepId | null {
  const at = stepsFor(a).indexOf(current);
  return at > 0 ? stepsFor(a)[at - 1] : null;
}

/** `index` is the number of steps behind the visitor and `total` counts
 * the questions plus the submission, so the bar is never full before the
 * visitor submits. */
export function progress(
  a: Answers,
  current: QuestionStepId,
): { index: number; total: number } {
  const steps = stepsFor(a);
  return {
    index: Math.max(0, steps.indexOf(current)),
    total: steps.length + 1,
  };
}

/** Where a URL lands: the step it names, or, when it asks for a step the
 * answers have not earned (or one this walk does not have), the first
 * unanswered step in its place. */
export function landingStep(
  slug: string | undefined,
  a: Answers,
): { step: QuestionStepId; replace: boolean } {
  const fallback = firstUnansweredStep(a);
  if (slug === undefined) return { step: "start", replace: false };
  if (!isQuestionStep(slug)) return { step: fallback, replace: true };
  const steps = stepsFor(a);
  const at = steps.indexOf(slug);
  if (at === -1 || at > steps.indexOf(fallback)) {
    return { step: fallback, replace: true };
  }
  return { step: slug, replace: false };
}

export type Segment = "pre_need" | "at_need";
export type AvailabilityQuery = { zip: string; segment: Segment };

/** Whether anyone in the submission needs a case rather than a policy:
 * a death, or a person with 12 months or less (or on hospice). */
export function isAtNeedLead(a: Answers): boolean {
  if (a.situation === "death_occurred") return true;
  if (a.situation === null) return false;
  return (
    (includesSelf(a) && a.self.hospice === true) ||
    (includesLovedOne(a) && a.lovedOne.hospice === true)
  );
}

/** The bounded kind used for analytics and for choosing the call line. */
export type LeadKind = "death" | "imminent" | "planning";

export function leadKindOf(a: Answers): LeadKind {
  if (a.situation === "death_occurred") return "death";
  return isAtNeedLead(a) ? "imminent" : "planning";
}

function segmentFor(hospice: boolean | null): Segment {
  return hospice === true ? "at_need" : "pre_need";
}

/** The availability question a step's commit raises, if any. The self ZIP
 * is checked on its own step, as a policy market until the health answer
 * says it needs a case market, and again after that answer. */
export function availabilityQueryFor(
  a: Answers,
  step: QuestionStepId,
): AvailabilityQuery | null {
  if (step === "where" && isValidZip(a.where.zip)) {
    return { zip: a.where.zip, segment: "at_need" };
  }
  if ((step === "you" || step === "you-health") && isValidZip(a.self.zip)) {
    return { zip: a.self.zip, segment: segmentFor(a.self.hospice) };
  }
  if (step === "loved-details" && isValidZip(lovedOneZip(a))) {
    return { zip: lovedOneZip(a), segment: segmentFor(a.lovedOne.hospice) };
  }
  return null;
}

export type AvailabilityStatus = "in_area" | "out_of_area" | "unknown";

export function availabilityKey(query: AvailabilityQuery): string {
  return `${query.segment}:${query.zip}`;
}

/** An at-need ZIP we do not serve ends the walk there: the visitor can
 * only edit the ZIP. A pre-need one is passed once acknowledged. */
export function isBlockingSegment(segment: Segment): boolean {
  return segment === "at_need";
}

/** Whether an availability answer holds the visitor at its step. */
export function holdsVisitor(
  query: AvailabilityQuery,
  status: AvailabilityStatus | undefined,
  acknowledged: readonly string[],
): boolean {
  if (status !== "out_of_area") return false;
  return (
    isBlockingSegment(query.segment) ||
    !acknowledged.includes(availabilityKey(query))
  );
}

/** The earliest step in the walk whose availability answer is out of
 * area and holds the visitor: always for an at-need ZIP, until
 * acknowledged for a pre-need one. */
export function pendingOutOfArea(
  a: Answers,
  availability: Record<string, AvailabilityStatus>,
  acknowledged: readonly string[],
): { step: QuestionStepId; query: AvailabilityQuery } | null {
  for (const step of stepsFor(a)) {
    const query = availabilityQueryFor(a, step);
    if (!query || !isStepComplete(a, step)) continue;
    if (
      holdsVisitor(query, availability[availabilityKey(query)], acknowledged)
    ) {
      return { step, query };
    }
  }
  return null;
}

export type Route =
  | { kind: "step"; step: QuestionStepId; replace: boolean }
  | { kind: "out-of-area" };

/** Where a question URL lands once availability is known: the step
 * `landingStep` picks, unless that step lies beyond an at-need ZIP we do
 * not serve, in which case the out-of-area screen, so going Back and
 * forward again never gets past it. */
export function routeFor(
  slug: string | undefined,
  a: Answers,
  availability: Record<string, AvailabilityStatus>,
): Route {
  const landing = landingStep(slug, a);
  const steps = stepsFor(a);
  for (const step of steps) {
    if (step === landing.step) break;
    const query = availabilityQueryFor(a, step);
    if (!query || !isBlockingSegment(query.segment)) continue;
    if (!isStepComplete(a, step)) continue;
    if (availability[availabilityKey(query)] === "out_of_area") {
      return { kind: "out-of-area" };
    }
  }
  return { kind: "step", ...landing };
}

export type BackAction =
  /** The previous history entry is the previous step: just go back. */
  | { kind: "history" }
  /** The visitor arrived here some other way: step back by replacing. */
  | { kind: "replace"; step: QuestionStepId };

/** What the Back control does. `cameFrom` is the step the visitor
 * navigated from to reach this entry, when the flow itself pushed it. */
export function backAction(
  a: Answers,
  current: QuestionStepId,
  cameFrom: string | null,
): BackAction | null {
  const previous = prevStep(a, current);
  if (previous === null) return null;
  return cameFrom === previous
    ? { kind: "history" }
    : { kind: "replace", step: previous };
}
